import React, { useState } from 'react';
import {
  useDropLinks,
  createDropLink,
  revokeDropLink,
  reactivateDropLink,
  deleteDropLink,
} from '../data/firestore';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

const dropUrl = (token) => `${window.location.origin}/u/${token}`;

const expiryLabel = (ms) => {
  if (!ms) return '';
  const d = Math.ceil((ms - Date.now()) / 86400000);
  if (d < 0) return 'expired';
  if (d === 0) return 'expires today';
  return `expires in ${d} day${d === 1 ? '' : 's'}`;
};

export default function DropLinkManager({ item }) {
  const links = useDropLinks(item.id);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestNote, setGuestNote] = useState('');
  const [passcode, setPasscode] = useState('');
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState('');

  const create = async () => {
    setBusy(true);
    try {
      await createDropLink(item, {
        label: label.trim(),
        guestName: guestName.trim(),
        guestNote: guestNote.trim(),
        passcode: passcode.trim(),
        expiresDays: days,
      });
      setLabel('');
      setGuestName('');
      setGuestNote('');
      setPasscode('');
      setCreating(false);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(dropUrl(token));
      setCopied(token);
      setTimeout(() => setCopied(''), 1500);
    } catch (_e) {
      /* clipboard blocked — user can select manually */
    }
  };

  const active = (links || []).filter((l) => l.status !== 'revoked' && (!l.expiresAt || l.expiresAt > Date.now()));
  const inactive = (links || []).filter((l) => l.status === 'revoked' || (l.expiresAt && l.expiresAt <= Date.now()));

  return (
    <div className="cs-droplinks">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Share an upload link</h2>
        <span className="cs-help">Let a videographer add footage without an account</span>
        <div style={{ marginLeft: 'auto' }}>
          {!creating && (
            <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => setCreating(true)}>
              ＋ New upload link
            </button>
          )}
        </div>
      </div>

      {creating && (
        <div className="cs-droplink-form">
          <label>
            Label (who's it for?)
            <input className="cs-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Carlos — water cam" />
          </label>
          <label>
            Pre-fill their name (optional)
            <input
              className="cs-input"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="e.g. Carlos — saves them typing it"
            />
          </label>
          <label>
            Note for them (optional)
            <input
              className="cs-input"
              value={guestNote}
              onChange={(e) => setGuestNote(e.target.value)}
              placeholder="e.g. Please drop all the .RDC folders from Sat"
            />
          </label>
          <label>
            Passcode (optional)
            <input className="cs-input" value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="leave blank for none" />
          </label>
          <label>
            Expires
            <select className="cs-input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>in 7 days</option>
              <option value={14}>in 14 days</option>
              <option value={30}>in 30 days</option>
            </select>
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <button className="cs-btn cs-btn-primary" onClick={create} disabled={busy}>
              {busy ? 'Creating…' : 'Create link'}
            </button>
            <button className="cs-btn cs-btn-ghost" onClick={() => setCreating(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {links === null ? (
        <p className="cs-help" style={{ marginTop: 12 }}>Loading…</p>
      ) : links.length === 0 && !creating ? (
        <p className="cs-help" style={{ marginTop: 12 }}>
          No links yet. Create one and send it to your videographer — their footage (incl. RED <code>.RDC</code> folders)
          lands straight in this workspace.
        </p>
      ) : (
        <div style={{ marginTop: 12 }}>
          {[...active, ...inactive].map((l) => {
            const dead = l.status === 'revoked' || (l.expiresAt && l.expiresAt <= Date.now());
            return (
              <div key={l.id} className={`cs-droplink-row ${dead ? 'dead' : ''}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {l.label || 'Upload link'} {l.passcode ? <span className="cs-badge" title="Passcode protected">🔒</span> : null}
                    {dead && <span className="cs-badge" style={{ marginLeft: 6 }}>{l.status === 'revoked' ? 'off' : 'expired'}</span>}
                  </div>
                  <div className="cs-droplink-url" title={dropUrl(l.token)}>{dropUrl(l.token)}</div>
                  <div className="cs-help" style={{ fontSize: 12 }}>
                    {l.uploadedCount || 0} file{(l.uploadedCount || 0) === 1 ? '' : 's'} · {fmtBytes(l.uploadedBytes)} received
                    {l.lastContributor ? ` · last from ${l.lastContributor}` : ''}
                    {l.expiresAt ? ` · ${expiryLabel(l.expiresAt)}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {!dead && (
                    <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => copy(l.token)}>
                      {copied === l.token ? 'Copied ✓' : 'Copy link'}
                    </button>
                  )}
                  {!dead ? (
                    <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => revokeDropLink(l.token)} title="Turn this link off" data-agent-noclick>
                      Revoke
                    </button>
                  ) : l.status === 'revoked' ? (
                    <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => reactivateDropLink(l.token, { expiresDays: 14 })}>
                      Reactivate
                    </button>
                  ) : null}
                  <button
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    onClick={() => deleteDropLink(l.token)}
                    title="Delete this link permanently"
                    data-agent-noclick
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
